import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { PageHeader } from "@/components/site/PageHeader";
import { formatCents } from "@/lib/format";
import { toast } from "sonner";
import { Package, LogOut, RotateCcw } from "lucide-react";
import { addToCart } from "@/lib/cart";

export const Route = createFileRoute("/_authenticated/account")({
  head: () => ({
    meta: [
      { title: "Your account — Glow Long Golf" },
      { name: "description", content: "Your Glow Long Golf account. View past orders, reorder glow balls, manage your shipping address, and sign out." },
    ],
  }),
  component: AccountPage,
});

type OrderRow = {
  id: string;
  created_at: string;
  amount_total_cents: number;
  currency: string;
  status: string;
  refund_amount_cents: number;
  dispute_status: string | null;
  shipping: any;
  order_items: Array<{
    product_title: string;
    variant_name: string;
    quantity: number;
    unit_price_cents: number;
  }>;
};

function AccountPage() {
  const { data: userData } = useQuery({
    queryKey: ["me"],
    queryFn: async () => (await supabase.auth.getUser()).data.user,
  });

  const { data: orders, isLoading } = useQuery({
    queryKey: ["my-orders"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("orders")
        .select("id, created_at, amount_total_cents, currency, status, refund_amount_cents, dispute_status, shipping, order_items(product_title, variant_name, quantity, unit_price_cents)")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as unknown as OrderRow[];
    },
  });

  async function signOut() {
    await supabase.auth.signOut();
    toast.success("Signed out");
    window.location.href = "/";
  }

  function statusBadge(o: OrderRow) {
    if (o.dispute_status) return { label: `Disputed: ${o.dispute_status}`, cls: "bg-destructive/15 text-destructive" };
    if (o.refund_amount_cents > 0 && o.refund_amount_cents >= o.amount_total_cents)
      return { label: "Refunded", cls: "bg-muted text-muted-foreground" };
    if (o.refund_amount_cents > 0) return { label: "Partially refunded", cls: "bg-amber-500/15 text-amber-700" };
    if (o.status === "paid") return { label: "Paid", cls: "bg-primary/15 text-primary" };
    if (o.status === "failed") return { label: "Payment failed", cls: "bg-destructive/15 text-destructive" };
    return { label: o.status, cls: "bg-muted text-muted-foreground" };
  }

  return (
    <>
      <PageHeader eyebrow="Account" title="Your account" lead={userData?.email ?? ""} />
      <section className="mx-auto max-w-4xl px-4 pb-20 sm:px-6 lg:px-8">
        <div className="mb-6 flex items-center justify-between">
          <h2 className="font-display text-xl font-semibold">Order history</h2>
          <button onClick={signOut} className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-4 py-2 text-sm hover:bg-secondary">
            <LogOut className="h-4 w-4" /> Sign out
          </button>
        </div>

        {isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}

        {!isLoading && (!orders || orders.length === 0) && (
          <div className="rounded-2xl border border-dashed border-border bg-card/40 p-10 text-center">
            <Package className="mx-auto h-10 w-10 text-muted-foreground" />
            <p className="mt-3 text-sm text-muted-foreground">You haven't placed any orders yet.</p>
            <Link to="/shop" className="mt-5 inline-flex rounded-full bg-primary px-5 py-2 text-sm font-semibold text-primary-foreground shadow-glow hover:opacity-90">
              Browse the shop
            </Link>
          </div>
        )}

        <ul className="space-y-4">
          {orders?.map((o) => {
            const badge = statusBadge(o);
            return (
              <li key={o.id} className="rounded-2xl border border-border bg-card p-5">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <p className="text-xs uppercase tracking-wider text-muted-foreground">Order</p>
                    <p className="font-mono text-sm">#{o.id.slice(0, 8)}</p>
                    <p className="mt-1 text-xs text-muted-foreground">{new Date(o.created_at).toLocaleDateString()}</p>
                  </div>
                  <span className={`rounded-full px-3 py-1 text-xs font-medium ${badge.cls}`}>{badge.label}</span>
                  <div className="text-right">
                    <p className="text-xs uppercase tracking-wider text-muted-foreground">Total</p>
                    <p className="font-semibold">{formatCents(o.amount_total_cents)}</p>
                    {o.refund_amount_cents > 0 && (
                      <p className="text-xs text-muted-foreground">Refunded {formatCents(o.refund_amount_cents)}</p>
                    )}
                  </div>
                </div>
                <ul className="mt-4 divide-y divide-border border-t border-border">
                  {o.order_items?.map((i, idx) => (
                    <li key={idx} className="flex items-center justify-between py-2 text-sm">
                      <span>
                        <span className="font-medium">{i.quantity}× </span>
                        {i.product_title}
                        {i.variant_name && <span className="text-muted-foreground"> — {i.variant_name}</span>}
                      </span>
                      <span className="text-muted-foreground">{formatCents(i.unit_price_cents * i.quantity)}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-4 flex justify-end">
                  <button
                    onClick={() => {
                      o.order_items?.forEach((i) =>
                        addToCart({
                          variantId: `reorder-${i.product_title}-${i.variant_name}`,
                          productSlug: "",
                          productTitle: i.product_title,
                          variantName: i.variant_name,
                          unitPriceCents: i.unit_price_cents,
                          quantity: i.quantity,
                        }),
                      );
                      toast.success("Items added to cart");
                    }}
                    className="inline-flex items-center gap-2 rounded-full border border-border bg-background px-4 py-2 text-xs font-medium hover:bg-secondary"
                  >
                    <RotateCcw className="h-3 w-3" /> Reorder
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      </section>
    </>
  );
}
